import { useState, useEffect, useCallback } from 'react';
import marketDataService from '../services/marketDataService';

/**
 * Enhanced hook for real-time market data with improved error handling
 * @param {string} activeMarket - The active market type ('crypto' or 'stocks')
 * @returns {Object} Market data state and controls
 */
const useRealTimeMarketData = (activeMarket = 'crypto') => {
  const [marketData, setMarketData] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [marketStats, setMarketStats] = useState(null);

  // Handle market data updates
  const handleMarketUpdate = useCallback((event) => {
    try {
      if (event?.type === 'data') {
        setMarketData(prevData => {
          const existingIndex = prevData?.findIndex(item => item?.symbol === event?.symbol);
          
          if (existingIndex >= 0) {
            // Update existing item
            const newData = [...prevData];
            newData[existingIndex] = { ...newData?.[existingIndex], ...event?.data };
            return newData;
          } else {
            // Add new item
            return [...prevData, event?.data];
          }
        });
        
        setLastUpdate(new Date());
        setError(null);
      } else if (event?.type === 'status') {
        setConnectionStatus(event?.status || 'disconnected');
        
        if (event?.message && event?.status === 'error') {
          setError(event?.message);
        } else {
          setError(null);
        }
      }
    } catch (err) {
      console.error('Error handling market update:', err);
      setError('Failed to process market data update');
    }
  }, []);

  // Connect to market data service
  const connect = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 Connecting to market data service...');
      await marketDataService?.connect(activeMarket);
      
      // Get initial data
      const initialData = marketDataService?.getAllMarketData();
      if (initialData?.length > 0) {
        setMarketData(initialData);
      }
      
      console.log('✅ Market data service connected successfully');
    } catch (err) {
      console.error('❌ Failed to connect to market data service:', err);
      setError(err?.message || 'Failed to connect to market data service');
      setConnectionStatus('error');
    } finally {
      setLoading(false);
    }
  }, [activeMarket]);

  // Disconnect from service
  const disconnect = useCallback(() => {
    console.log('🔌 Disconnecting from market data service...');
    marketDataService?.disconnect();
    setConnectionStatus('disconnected');
    setError(null);
  }, []);

  // Refresh market data
  const refresh = useCallback(async () => {
    try {
      await disconnect();
      await new Promise(resolve => setTimeout(resolve, 1000)); // Brief pause
      await connect();
    } catch (err) {
      console.error('❌ Failed to refresh market data:', err);
      setError('Failed to refresh market data');
    }
  }, [connect, disconnect]);

  // Get market statistics
  const updateMarketStats = useCallback(() => {
    try {
      const stats = marketDataService?.getMarketStats(activeMarket);
      setMarketStats(stats);
    } catch (err) {
      console.error('❌ Failed to get market stats:', err);
    }
  }, [activeMarket]);

  // Initial connection setup
  useEffect(() => {
    connect();

    // Subscribe to market data updates
    const unsubscribe = marketDataService?.subscribe(handleMarketUpdate);

    // Update market stats periodically
    const statsInterval = setInterval(updateMarketStats, 10000); // Every 10 seconds

    return () => {
      unsubscribe?.();
      clearInterval(statsInterval);
      disconnect();
    };
  }, [connect, disconnect, handleMarketUpdate, updateMarketStats]);

  // Update stats when data changes
  useEffect(() => {
    if (marketData?.length > 0) {
      updateMarketStats();
    }
  }, [marketData, updateMarketStats]);

  // Get specific symbol data
  const getSymbolData = useCallback((symbol) => {
    return marketData?.find(item => item?.symbol === symbol) || null;
  }, [marketData]);

  // Get top gainers
  const getTopGainers = useCallback((limit = 5) => {
    return [...marketData]
      ?.sort((a, b) => (b?.change24h || 0) - (a?.change24h || 0))
      ?.slice(0, limit);
  }, [marketData]);

  // Get top losers
  const getTopLosers = useCallback((limit = 5) => {
    return [...marketData]
      ?.sort((a, b) => (a?.change24h || 0) - (b?.change24h || 0))
      ?.slice(0, limit);
  }, [marketData]);

  // Get service status
  const getServiceStatus = useCallback(() => {
    return marketDataService?.getStatus() || {
      isConnected: false,
      status: 'disconnected',
      quality: 'unknown',
      lastUpdate: null,
      activeConnections: 0,
      cachedSymbols: 0,
      fallbackActive: true
    };
  }, []);

  return {
    // Data
    marketData,
    marketStats,
    connectionStatus,
    lastUpdate,
    error,
    loading,
    
    // Controls
    connect,
    disconnect,
    refresh,
    
    // Utilities
    getSymbolData,
    getTopGainers,
    getTopLosers,
    getServiceStatus,
    
    // Status helpers
    isConnected: connectionStatus === 'connected' || connectionStatus === 'degraded',
    isConnecting: connectionStatus === 'connecting',
    hasError: !!error,
    fallbackActive: getServiceStatus()?.fallbackActive || false
  };
};

export default useRealTimeMarketData;